"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Clock, Activity, ChevronDown, ChevronRight, Database } from "lucide-react";
import { MarkdownComponents } from "./markdown-components";

interface SummaryContentProps {
  displayedContent: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  showMetadata?: boolean;
  summary?: any; // SummaryData from ai-summary-context
}

/**
 * Collapsible summary content component
 */
export function SummaryContent({
  displayedContent,
  isExpanded,
  onToggleExpanded,
  showMetadata = true,
  summary,
}: SummaryContentProps) {
  if (!displayedContent) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Teaser Bar - Always Visible */}
      <div
        className="flex items-center justify-between p-3 bg-card-50 dark:bg-card-800 rounded-lg cursor-pointer hover:bg-card-100 dark:hover:bg-card-700 transition-colors"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-sm md:text-base font-medium">AI Summary Ready</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs md:text-sm text-muted-foreground">Click to expand</span>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Full Content - Collapsible */}
      {isExpanded && (
        <div className="space-y-3">
          <MarkdownComponents markdownContent={displayedContent} />

          {showMetadata && summary?.metadata && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {summary.metadata.cacheHit && (
                <Badge variant="secondary" className="text-xs" title="Timeline or sanitized data was served from cache">
                  <Database className="h-3 w-3 mr-1" />
                  From cache
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                <Activity className="h-3 w-3 mr-1" />
                {summary.metadata.activityCount} activities
              </Badge>
              {summary.metadata.processingTime > 0 && (
                <Badge variant="secondary" className="text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  {summary.metadata.processingTime}ms
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                {new Date(summary.createdAt).toLocaleString()}
              </Badge>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
